'use client';

import React, { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The page's content, faded out and the next page faded in when a Next
 * `<Link>` or `router.push` moves to another path. base.css styles the class
 * `page-swap`; full page loads are animated there too, through
 * `@view-transition`, without this component.
 *
 * Next runs every navigation inside a React transition, and React calls
 * `document.startViewTransition` itself whenever a `<ViewTransition>` boundary
 * it commits has a class other than `none`. So the class is `page-swap` only
 * for the render that changes the pathname, and only for visitors who have not
 * asked for reduced motion: any other transition inside the page (a filter
 * that changes only the query string, a `useTransition` form) renders `none`
 * and swaps in place, as before. Back and forward swap instantly whatever the
 * class, because React renders a popstate update synchronously.
 *
 * The boundary's name changes with every new path, so the browser captures
 * the old page and the new one as two separate groups, each held where it is
 * on screen. Under one name the group would animate from the old page's box
 * to the new one's, and after scrolling down a long page that slides the
 * whole page by the scroll distance.
 *
 * The last path shown is read during render, which React advises against
 * because a render may be thrown away; here that is safe, because it is only
 * written after a commit, so a discarded render reads the same value again.
 *
 * Only the React Next bundles for the App Router exports `ViewTransition`; the
 * stable `react` package that Jest and Storybook load does not, so there the
 * content renders bare rather than as an invalid element.
 */
export const PageTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pathname = usePathname();
  const lastShown = useRef({ pathname, pathNumber: 0 });

  const pathChanged = pathname !== lastShown.current.pathname;
  const pathNumber = pathChanged ? lastShown.current.pathNumber + 1 : lastShown.current.pathNumber;
  const animate = pathChanged && !prefersReducedMotion();

  useEffect(() => {
    lastShown.current = { pathname, pathNumber };
  }, [pathname, pathNumber]);

  if (!('ViewTransition' in React) || !React.ViewTransition) {
    return <>{children}</>;
  }

  return (
    <React.ViewTransition name={`page-${pathNumber}`} default="none" update={animate ? 'page-swap' : 'none'}>
      {children}
    </React.ViewTransition>
  );
};
